import { SceneSetters } from "../../types";
import mapData from "../../data/tilemap.json";
import { STARTING_LEVEL, dropRecord, enemyRecord } from "../../data/settings";
import { sample } from "../../utils/array";

// prettier-ignore
const PATH = [
  [18, 12], [17, 12], [16, 12], [15, 12], [14, 12], [13, 12], [12, 12], [11, 12],
  [11, 13], [11, 14], [11, 15], [11, 16], [11, 17], [11, 18],
  [10, 18], [9, 18], [8, 18], [7, 18], [6, 18], [5, 18],
  [5, 17],
].map(([x, y]) => [x * 10 +10, y * 10 -10]);
const PATH_OFFSET = 0;

const sceneConfig: Phaser.Types.Scenes.SettingsConfig = {
  active: false,
  visible: false,
  key: "Game",
};
const textStyle = {
  color: "white",
  fontSize: "xx-small",
  width: "100%",
  margin: 0,
  marginLeft: "30%",
  textAlign: "center",
};

type Sprite = Phaser.GameObjects.GameObject & { body: any };
type KeyMap<T extends string> = Record<T, Phaser.Input.Keyboard.Key>;

export class GameScene extends Phaser.Scene {
  private cursorKeys?: Phaser.Types.Input.Keyboard.CursorKeys;
  private helperKeys?: KeyMap<"Space" | "R">;
  private map?: Phaser.Tilemaps.Tilemap;
  private tileset?: Phaser.Tilemaps.Tileset;
  private terrain?: Phaser.Tilemaps.StaticTilemapLayer;
  private villagers?: Phaser.Physics.Arcade.Group;
  private enemies?: Phaser.Physics.Arcade.Group;
  private drops?: Phaser.Physics.Arcade.Group;
  private boxes?: Phaser.Physics.Arcade.Group;
  private text?: [Phaser.GameObjects.Rectangle, Phaser.GameObjects.DOMElement];
  private timer?: Phaser.Time.TimerEvent;
  private setter: SceneSetters;
  private isDragging: boolean;
  private level: number;

  constructor(setter: SceneSetters) {
    super(sceneConfig);
    this.setter = setter;
    this.isDragging = false;
    this.level = 1;
  }
  // prettier-ignore
  public preload() {
    this.load.tilemapTiledJSON("map", mapData);
    this.load.image("tiles", `${process.env.PUBLIC_URL}/tilesheet.png`);
    // entities
    this.load.image("red_villager", `${process.env.PUBLIC_URL}/red_villager.png`);
    this.load.image("blue_villager", `${process.env.PUBLIC_URL}/blue_villager.png`);
    this.load.image("cat", `${process.env.PUBLIC_URL}/cat.png`);
    this.load.image("bird", `${process.env.PUBLIC_URL}/bird.png`);
    // drops
    this.load.image("apple", `${process.env.PUBLIC_URL}/apple.png`);
    this.load.image("bad-banana", `${process.env.PUBLIC_URL}/bad-banana.png`);
    // weapons
    this.load.image("dagger", `${process.env.PUBLIC_URL}/dagger.png`);
    this.load.image("red_dagger", `${process.env.PUBLIC_URL}/red_dagger.png`);
    this.load.image("blue_dagger", `${process.env.PUBLIC_URL}/blue_dagger.png`);
    // other
    this.load.image("bin", `${process.env.PUBLIC_URL}/bin.png`);
    this.load.audio("theme_song", `${process.env.PUBLIC_URL}/acid_jazz.mp3`);
  }

  public create() {
    // setup Tiled map
    this.map = this.make.tilemap({ key: "map" });
    this.tileset = this.map.addTilesetImage("tilesheet", "tiles");
    this.map.createStaticLayer("terrain", this.tileset, 5, 5);
    this.map.createStaticLayer("ground", this.tileset, 5, 5);

    // setup groups (villagers, drops, boxes)
    this.villagers = this.physics.add.group({
      immovable: true,
      frictionX: 1,
      frictionY: 1,
      collideWorldBounds: true,
    });
    this.drops = this.physics.add.group({ immovable: true });
    this.boxes = this.physics.add.group({ collideWorldBounds: true });
    // prettier-ignore
    this.enemies = this.physics.add.group({ immovable: true, collideWorldBounds: true });

    // add collisions
    this.physics.add.collider(this.villagers, this.boxes);
    this.physics.add.overlap(this.villagers, this.drops, (villager, drop) => {
      //@ts-ignore
      if (!drop.visible) return;
      const { velocity } = villager.body;
      // if the villager did not move into this drop, ignore this overlap
      if (velocity.x === 0 && velocity.y === 0) return;

      const [color, base] = drop.name.split("_");
      const [villagerColor] = villager.name.split("_");

      // drop has a color, but villager isn't the opposite
      if (base) {
        if (color === villagerColor) return;
        else {
          //@ts-ignore
          drop.setTexture(base).setName(base);
        }
      }

      //@ts-ignore
      drop.setVisible(false);
      this.repositionDrops();

      // apply effect
      const entry = dropRecord[drop.name];
      if (entry?.damage)
        this.setter.enemy((v) => Math.max(v - entry?.damage, 0));
      else if (entry?.hp)
        this.setter.hp((v) => (v + entry?.hp >= 100 ? 100 : v + entry?.hp));

      // increase collection
      //@ts-ignore
      this.setter.drops((dic) => ({
        ...dic,
        [drop.name]: (dic[drop.name] ?? 0) + 1,
      }));
    });
    this.physics.add.overlap(this.boxes, this.drops, (box, drop) => {
      //@ts-ignore
      drop.setVisible(false);
    });

    // other (setup keyboard keys, etc)
    this.cursorKeys = this.input.keyboard.createCursorKeys();
    //prettier-ignore
    this.helperKeys = this.input.keyboard.addKeys("Space,R") as KeyMap<"Space"|"R">;

    this.timer = this.time.addEvent({});
    this.text = [
      this.add.rectangle(0, 100, 360, 40, 0x000).setAlpha(0.5),
      this.add.dom(0, 100, "p", textStyle, "level 1"),
    ];
    this.text[1].transformOnly = true;
    this.populateLevel(STARTING_LEVEL);
  }
  private populateLevel(level = 1) {
    this.level = level;
    // fade in and display current level
    this.cameras.main.fadeIn(2000);
    this.text?.forEach((o) => o.setAlpha(1));
    // TODO
    // trying to only set the innerText or innerHTML of a DOMObject results in weird issues
    // so we destroy it and recreate it from scratch
    if (this.text) {
      this.text[1]?.destroy();
      this.text[1] = this.add.dom(0, 100, "p", textStyle, `level ${level}`);
    }
    this.cameras.main.once("camerafadeincomplete", () => {
      this.tweens.add({
        targets: this.text,
        alpha: 0,
        duration: 1000,
        ease: "Power2",
      });
      this.sound.play("theme_song", { loop: true });
    });

    if (!this.map || !this.tileset || !this.villagers || !this.boxes) return;
    // FIXME: this assume that you always progress in terms of levels
    // some more logic would be needed for random terrain traversal
    //add tile layer and remove old tile layer
    if (level === 1 || level >= 5 || this.terrain === undefined) {
      const layerName = level < 5 ? 1 : level;
      // remove collision from previous layer
      this.terrain?.setCollisionByExclusion([], false);
      this.terrain?.setVisible(false);
      //prettier-ignore
      this.terrain = this.map.createStaticLayer(`level${layerName}`, this.tileset, 5, 5);
      this.terrain.setCollision([265 + 1, 76 + 1, 105 + 1], true);
      this.physics.add.collider(this.villagers, this.terrain);
      this.physics.add.overlap(this.boxes, this.terrain, (box, terrain) => {
        //@ts-ignore
        if (terrain.index > 0) {
          this.isDragging = false;
          // the box name contains the initial position of this box
          // which is where we want to go
          const [x, y] = box.name.split(",").slice(1).map(Number);
          //@ts-ignore
          box.setGravity(0, 0).setVelocity(0, 0).setPosition(x, y);
        }
      });
    }

    //@ts-ignore
    // kill and hide all current groups
    const kill = (member: Sprite) => member.setActive(false).setVisible(false);
    this.setGroupProp(kill, this.villagers);
    this.setGroupProp(kill, this.enemies);
    this.setGroupProp(kill, this.drops);
    this.setGroupProp(kill, this.boxes);
    if (!this.map) return;

    // add sprites from Tiled object layer to our groups
    const layer = this.map?.getObjectLayer(`level${level}`);
    if (!layer) return;

    const { objects } = layer;
    objects.forEach((mapObject) => {
      const [x, y] = [(mapObject.x ?? 0) + 10, mapObject.y ?? 0];
      let group: Phaser.Physics.Arcade.Group | undefined;
      const { name, type } = mapObject;
      if (type === "villager") group = this.villagers;
      else if (type === "enemy") group = this.enemies;
      else if (type === "drop") group = this.drops;
      else if (type === "box") group = this.boxes;
      if (group)
        group
          .getFirstDead(true, x, y, name)
          .setVisible(true)
          .setActive(true)
          .setTexture(name)
          .setName(type === "box" ? `${name},${x},${y}` : name);
    });

    // reset timers (enemy paths)
    //prettier-ignore
    const timerConfig = { delay: 1500, callback: this.enemyLoop, callbackScope: this, loop: true };
    this.timer?.reset(timerConfig);
  }

  public update() {
    //handle  up/down cursor keys
    if (this.cursorKeys?.up?.isDown) this.setVillagerVelocity(0, -40);
    else if (this.cursorKeys?.down?.isDown) this.setVillagerVelocity(0, 40);
    else this.setGroupProp((villager) => villager.body.setVelocityY(0));

    //handle  left/right cursor keys
    if (this.cursorKeys?.right?.isDown) this.setVillagerVelocity(40, 0);
    else if (this.cursorKeys?.left?.isDown) this.setVillagerVelocity(-40, 0);
    else this.setGroupProp((villager) => villager.body.setVelocityX(0));

    //handle dragging boxes
    const space = this.helperKeys?.Space;
    if (space && Phaser.Input.Keyboard.JustDown(space))
      this.isDragging = !this.isDragging;
    this.setGroupProp((box: any) => {
      const isBoxTouching = box.body.touching;
      if (this.isDragging) {
        if (isBoxTouching?.right) box.setGravity(40, 0).setVelocity(0, 0);
        else if (isBoxTouching?.left) box.setGravity(-200, 0).setVelocity(0, 0);
        else if (isBoxTouching?.up) box.setGravity(0, -200).setVelocity(0, 0);
        else if (isBoxTouching?.down) box.setGravity(0, 200).setVelocity(0, 0);
      } else box.setGravity(0, 0).setVelocity(0, 0);
    }, this.boxes);

    //handle repositioning villagers
    const rKey = this.helperKeys?.R;
    if (rKey && Phaser.Input.Keyboard.JustDown(rKey)) {
      this.repositionVillagers();
      this.repositionDrops();
    }
  }
  // setup a common property for each Sprite in a group
  // (our groups are villagers, opponents and drops)
  private setGroupProp(f: (a: Sprite) => void, group = this.villagers) {
    if (group) Phaser.Actions.Call(group?.getChildren(), f, null);
  }
  private setVillagerVelocity(x: number, y: number) {
    if (this.villagers)
      Phaser.Actions.Call(
        this.villagers?.getChildren(),
        (villager) => {
          if (!("setVelocity" in villager.body)) return;
          // @ts-ignore
          const isRed = villager.name.startsWith("red_");
          if (isRed && y !== 0) return;
          if (!isRed && x !== 0) return;

          // avoid villagers overlapping with one another
          const hasOverlap = this.physics.overlap(
            villager,
            this.villagers,
            (left, right) => {
              if ("setVelocity" in left.body) left.body.setVelocity(-x, -y);
              if ("setVelocity" in right.body) right.body.setVelocity(x, y);
            }
          );
          if (!hasOverlap) villager.body.setVelocity(x, y);
        },
        null
      );
  }
  // an enemy goes through a path and then starts damaging the fortress HP
  private enemyLoop() {
    const count = 999999999999 - (this.timer?.getRepeatCount() ?? 0);
    const index = count + PATH_OFFSET;

    // we are staying (so reducer HP of the fortress)
    if (index >= PATH.length) {
      const enemies = this.enemies?.getChildren();
      const enemy = enemies?.[0] ?? { name: "" };
      const hpDamage = enemyRecord[enemy?.name]?.damage ?? 1;
      this.setter.hp((hp) => hp - hpDamage);
      // only screenshake the first time to signal you are staying
      // doing it more than that feels bad
      if (index === PATH.length) this.cameras.main.shake();
      return;
    }

    // get next position in our route (PATH)
    this.setGroupProp(
      // @ts-ignore
      (enemy) => enemy.setPosition(...PATH[index]),
      this.enemies
    );
  }
  private getFreeTile() {
    const filter = (tile: any) => tile.index < 0;
    const freeTiles = this.terrain?.filterTiles(filter, undefined, 1, 1, 8, 12);
    if (!freeTiles || freeTiles.length < 1) return [];
    return freeTiles;
  }

  private repositionGroupOnLayer(group: Phaser.Physics.Arcade.Group) {
    // filter active otherwise you may reuse those that were not made in this level
    const members = group.getChildren().filter((member) => member.active);
    const picks = sample(this.getFreeTile(), members.length);
    picks.forEach((tile, i) => {
      members[i]
        // @ts-ignore
        .setPosition(tile.pixelX + 10, tile.pixelY + 10)
        .setVisible(true);
    });
  }
  private repositionDrops() {
    if (!this.drops) return;
    this.repositionGroupOnLayer(this.drops);
  }

  private repositionVillagers() {
    if (!this.villagers) return;
    this.repositionGroupOnLayer(this.villagers);
    this.setter.hp((hp) => hp - 5);
  }
}
